import {
  ActionIcon,
  Badge,
  Button,
  Group,
  HoverCard,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../stores/LanguageStore";
import { useForm } from "react-hook-form";
import { useState } from "react";
import SharingController from "../../../components/Sharing/SharingController";
import { IconEdit, IconShare3 } from "@tabler/icons-react";
import PopOver from "../../../components/View/PopOver";

export const SharingEditor = ({
  editFunction,
  row,
  apiRef,
  field,
  readOnly,
  closePopover,
  onFinish,
  allowResellerCustomers,
}) => {
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(false);
  const sharingForm = useForm({
    defaultValues: {
      sharing: row[field]?.map((x) => ({
        ...x,
        sharing_targets: x?.sharing_targets?.map((sharing_target) => ({
          ...sharing_target,
          option_name: sharing_target.name,
        })),
      })),
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    const sharing = sharingForm
      .getValues()
      .sharing.filter((x) => x.sharing_targets.length > 0)
      .map((share) => ({
        ...share,
        ids_list: share.sharing_targets.map((x) => x.id),
        sharing_targets: share.sharing_targets.map((x) => ({
          ...x,
          name: x.option_name,
        })),
      }));

    setLoading(true);
    editFunction({
      row,
      id: row.id,
      sharing,
    })
      .then((response) => {
        closePopover?.();
        onFinish?.(sharing);
        apiRef?.current?.updateRows([{ id: row.id, [field]: sharing }]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Stack
      component={"form"}
      onSubmit={handleSubmit}
      gap={"xs"}
      w={600}
      pt={16}
      px={8}
    >
      <SharingController
        form={sharingForm}
        readOnly={readOnly}
        allowResellerCustomers={allowResellerCustomers}
      />

      <Group wrap="nowrap" justify="end" mt="xs">
        <Button
          onClick={closePopover}
          size="sm"
          variant="subtle"
          disabled={loading}
        >
          {lang.components.closer.submit}
        </Button>
        <Button type="submit" loading={loading} size="sm">
          {lang.contact_lists.edit.dialog.submit_button}
        </Button>
      </Group>
    </Stack>
  );
};

export const sharingColumn = ({
  apiRef,
  editFunction,
  field = "sharing",
  readOnly,
  allowResellerCustomers,
  sortable = true,
  flex,
}) => {
  const lang = useLanguage.getState().language;
  return {
    field: field,
    headerName: lang.components.sharing.button_tooltip,
    cellClassName: "hover-source",
    width: 150,
    tabIndex: null,
    sortable,
    flex,
    filterable: false,
    valueGetter: ({ row }) => {
      let shared = false;
      row[field]?.forEach((x) => {
        if (x?.sharing_targets?.length > 0) shared = true;
      });

      return (
        (row?.from_sharing
          ? lang.components.sharing?.row?.from_sharing
          : lang.components.sharing?.row?.source) +
        (shared ? lang.components.sharing.row?.shared : "")
      ).slice(0, -2);
    },
    renderCell: ({ row }) => {
      if (row?.isChild) {
        return "";
      }
      let shared = false;
      row[field]?.forEach((x) => {
        if (x?.sharing_targets?.length > 0 || row[field]?.length > 0)
          shared = true;
      });
      const sharedToUser = row[field]?.filter(
        (x) => x.sharing_type_to === "User"
      );
      const sharedToTeam = row[field]?.filter(
        (x) => x.sharing_type_to === "Team"
      );
      const sharedToOrg = row[field]?.filter(
        (x) => x.sharing_type_to === "Organization"
      );
      const sharedToResellers = row[field]?.filter(
        (x) => x.sharing_type_to === "ResellerCustomers"
      );

      const getValues = (type) => {
        let values = "";
        if (type === "user") {
          sharedToUser?.forEach((share) => {
            share?.sharing_targets?.forEach((target) => {
              values += target.name + " #" + target.id + " | ";
            });
          });
        } else if (type === "team") {
          sharedToTeam?.forEach((share) => {
            share?.sharing_targets?.forEach((target) => {
              values += target.name + " #" + target.id + " | ";
            });
          });
        } else if (type === "org") {
          sharedToOrg?.forEach((share) => {
            share?.sharing_targets?.forEach((target) => {
              values += target.name + " #" + target.id + " | ";
            });
          });
        } else if (type === "reseller") {
          sharedToResellers?.forEach((share) => {
            share?.sharing_targets?.forEach((target) => {
              values += target.name + " #" + target.id + " | ";
            });
          });
        }
        return Array.from(new Set(values.split(" | ")))
          .join(", ")
          .toString()
          .slice(0, -2);
      };

      // const showModal = () => {
      //   const mainEdit = editFunction(row);

      //   modals.open({
      //     title: (
      //       <Text>
      //         {lang.components.sharing.title}: <b> {row.name} </b>
      //       </Text>
      //     ),
      //     children: (
      //       <SharingEditor
      //         apiRef={apiRef}
      //         field={field}
      //         editFunction={mainEdit}
      //         row={row}
      //         readOnly={readOnly}
      //       />
      //     ),
      //     size: "auto",
      //   });
      // };
      const mainEdit = editFunction(row);

      let createdBy = row?.created_by?.first_name
        ? `${row?.created_by?.first_name} #${row?.created_by?.id}`
        : `${lang.components.sharing.row.shared_by_user_id} #${row?.created_by}`;

      return (
        <Group gap={"xs"}>
          {row?.from_sharing &&
            (row.created_by ? (
              <Tooltip label={createdBy}>
                <Badge color="yellow" size="xs" radius={"xl"} py={9}>
                  {lang.components.sharing.row.from_sharing}
                </Badge>
              </Tooltip>
            ) : (
              <Tooltip label={lang.components.sharing.row.default_shared}>
                <Badge color="gray" size="xs" radius={"xl"} py={9}>
                  {lang.components.sharing.row.default}
                </Badge>
              </Tooltip>
            ))}
          {shared && (
            <HoverCard>
              <HoverCard.Target>
                <Badge color="green" size="xs" radius={"xl"} py={9}>
                  {lang.components.sharing.row.shared_chip}
                </Badge>
              </HoverCard.Target>

              <HoverCard.Dropdown>
                <Text fz={12}>
                  {lang.components.sharing.row.shared_with} <br />
                  {getValues("user").length > 0 ? (
                    <>
                      <b>{lang.components.sharing.row.users} : </b>{" "}
                      {getValues("user") || " - "} <br />
                    </>
                  ) : (
                    ""
                  )}
                  {getValues("team").length > 0 ? (
                    <>
                      <b>{lang.components.sharing.row.teams} : </b>{" "}
                      {getValues("team") || " - "} <br />
                    </>
                  ) : (
                    ""
                  )}
                  {getValues("org").length > 0 ? (
                    <>
                      <b>{lang.components.sharing.row.organizations} : </b>{" "}
                      {getValues("org") || " - "} <br />
                    </>
                  ) : (
                    ""
                  )}
                  {getValues("reseller").length > 0 ? (
                    <>
                      <b>{lang.users_list.resellers} : </b>{" "}
                      {getValues("reseller") || " - "} <br />
                    </>
                  ) : (
                    ""
                  )}
                </Text>
              </HoverCard.Dropdown>
            </HoverCard>
          )}

          {editFunction && (
            <PopOver
              opener={(openFunction, ref, opened) => (
                <Tooltip
                  label={
                    shared
                      ? lang.tags.edit.button_label
                      : lang.components.sharing.add
                  }
                >
                  <ActionIcon
                    variant="light"
                    className="to-blue"
                    size={"md"}
                    color="primary"
                    onClick={openFunction}
                    ref={ref}
                    style={{
                      opacity: opened ? 1 : undefined,
                    }}
                    disabled={
                      !row?.write_permission &&
                      !row?.permission?.write_permission
                    }
                  >
                    {shared ? <IconEdit size={16} /> : <IconShare3 size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
              popoverChildren={(closePopover) => (
                <SharingEditor
                  apiRef={apiRef}
                  editFunction={mainEdit}
                  field={field}
                  readOnly={readOnly}
                  row={row}
                  closePopover={closePopover}
                  allowResellerCustomers={allowResellerCustomers}
                />
              )}
              elevation={3}
              anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
              transformOrigin={{ horizontal: "center", vertical: "top" }}
            />
          )}
        </Group>
      );
    },
  };
};
